<template>
  <div class="pt-3 pb-5">
    <router-link :to="{name:'InsightsHealth'}" class="btn btn-outline-secondary btn-sm mb-3">
      <span class="bi bi-arrow-left me-2"></span>
      Back to Health
    </router-link>

    <h2 ref="inputAnchor">Source Performance</h2>
    <hr>

    <transition mode="out-in">
      <div class="d-grid mb-3">
        <button v-if="activeCat"
                @click="reset"
            class="btn btn-outline-secondary"
            type="button">
          <span class="text-truncate">
            <span class="bi bi-chevron-left me-1"></span>
            {{ activeCat.name || 'Uncategorized' }}
          </span>
        </button>
        <div v-else>
          <p class="lead">Select a source category:</p>
          <div class="list-group">
            <a v-for="cat in sources_data"
               :key="cat.id"
               class="list-group-item list-group-item-action d-flex align-items-center"
               :class="{'active': activeCat === cat}"
               href="#"
               @click.prevent="selectCat(cat)"
            >
                {{ cat.name || 'Uncategorized' }} <span class="ms-auto badge bg-info">{{ cat.item_count }}</span>
            </a>
          </div>
        </div>
      </div>
    </transition>

    <transition mode="out-in">
      <div v-if="activeCat" class="d-grid mb-5">
        <button
            v-if="chart_data.length"
            class="btn btn-outline-secondary"
            type="button"
            @click="resetSourceData"
        >
          <span class="text-truncate">
            <span class="bi bi-chevron-left me-1"></span>
            {{ locationName }}
          </span>
        </button>
        <div v-else>
          <p class="lead">Select a source location:</p>
          <div class="list-group">
            <a class="list-group-item list-group-item-action d-flex align-items-center"
               @click.stop.prevent="setActiveLoc({id:0,name:`All ${activeCat.name}`})"
               :class="{'active':sourceData.source_category_id === activeCat.id && sourceData.source_location_id === 0}">
              All {{ activeCat.name }} <span class="ms-auto badge bg-info">{{ activeCat.item_count }}</span>
            </a>

            <template v-if="activeCat.locations.length">
              <a
                  v-for="loc in activeCat.locations" :key="loc.id"
                  class="list-group-item list-group-item-action d-flex align-items-center"
                  :class="{'active': sourceData.source_location_id === loc.id}"
                  href="#"
                  @click.prevent="setActiveLoc(loc)"
              >
                {{ loc.name }} <span class="ms-auto badge bg-info">{{ loc.item_count }}</span>
              </a>
            </template>
          </div>
        </div>
      </div>
    </transition>


      <loading-spinner v-if="loading" />
      <template v-else-if="chart_data.length">
        <div class="d-flex justify-content-center gap-3 small">
          <div class="ms-auto">chart interval: </div>
          <template v-for="(i, idx) in ['year','month','week','day']" :key="i">
            <span v-if="idx > 0" class="text-muted">|</span>
            <span v-if="interval===i" class="fw-bold">{{ i }}</span>
            <a v-else href="#" @click="interval=i">{{ i }}</a>
          </template>
        </div>
        <apexchart
            height="400"
            :options="chartOptions"
            :series="chartSeries"
        />



        <table v-if="summary" class="table table-sm text-center small mt-4">
          <thead class="table-light">
          <tr>
            <th>Invested</th>
            <th>Net Profit to Date</th>
            <th>ROI to Date</th>
            <th>Break Even</th>
            <th>Items Remaining</th>
            <th>*Estimated Total Net</th>
          </tr>
          </thead>
          <tbody>
          <tr>
            <td>${{ summary.running_cost.toFixed(2) }}</td>
            <td :class="summary.running_net_proceeds - summary.running_cost >= 0 ? 'text-success' : 'text-danger'">${{ (summary.running_net_proceeds - summary.running_cost).toFixed(2) }}</td>
            <td :class="summary.roi >= 0 ? 'text-success' : 'text-danger'">{{ summary.roi.toFixed(1) }}%</td>
            <td>
              <template v-if="breakEvenDates.length">
                <span v-for="(d, i) in breakEvenDates" :key="d" class="text-success">{{ d }}<span v-if="i < breakEvenDates.length - 1">, </span></span>
              </template>
              <span v-else class="text-muted">—</span>
            </td>
            <td>{{ summary.remaining }}</td>
            <td class="text-info">${{ summary.estimated_net.toFixed(2) }}</td>
          </tr>
          </tbody>
        </table>
        <div class="text-end muted small fst-italic small">
          *Average price of items already sold items multiplied by items remaining.</div>


        <apexchart
            class="mt-4"
            height="300"
            :options="inventoryOptions"
            :series="inventorySeries"
        />
      </template>
      <div v-else-if="activeCat && activeCat.id === 0 && activeCat.item_count > 0" class="card bg-light shadow">
        <div class="card-body">
          <div class="mb-3 lead">
            We can't show you where your profits are really coming from until
            {{ activeCat.item_count > 1 ? `these ${ activeCat.item_count } items get` : '1 item gets' }}
            a source
          </div>

          <div>
            <router-link :to="{name: 'Inventory', query: { sourceCategory: '0' }}" class="btn btn-outline-primary">
              <span class="bi bi-tag-fill me-1"></span> Tag these items
            </router-link>
          </div>

          <div class="mt-5">
            <router-link :to="{name: 'Settings-Support'}" class="small">
              <span class="bi bi-play-btn-fill text-danger me-2"></span>
              Watch the &quot;Adding Source Details to Items&quot; video in support.
            </router-link>
          </div>
        </div>
      </div>

    </div>

</template>

<script setup>
import {ref, computed, defineAsyncComponent, watch, onMounted} from 'vue'
import { useStore } from 'vuex'

import LoadingSpinner from "@/app/components/Utils/LoadingSpinner.vue";

const apexchart = defineAsyncComponent(() =>
    import('vue3-apexcharts').then(module => module.default || module)
);

const store = useStore()
const loading = ref(false)
const sources_data = ref([])
const location_data = ref(null)
const interval = ref('month')
const activeCat = ref(null)
const activeLoc = ref(null)
const inputAnchor = ref(null)
const sourceData = ref({
  source_category_id: null,
  source_location_id: null
})

const locationName = computed(() => {
  if (!activeCat.value) return ''
  if (activeLoc.value) return activeLoc.value.name
  if (!sourceData.value.source_location_id) return chart_data.value.length ? `All ${activeCat.value.name || 'Uncategorized'}` :  'Select'
  return activeCat.value.locations.filter(l => l.id === sourceData.value.source_location_id).name
})

const chart_data = computed(() => {
  if (!location_data.value || location_data.value.length === 0) return []
  const rows = location_data.value.map(d => ({
    date: d.date,
    running_cost: parseFloat(d.running_cost || 0),
    //running_net: parseFloat(d.running_net || 0),
    running_net_proceeds: parseFloat(d.running_net_proceeds || 0),
  }))
  const zeroDate = rows.length ? prevPeriodLabel(rows[0].date, interval.value) : ''
  return [{ date: zeroDate, running_cost: 0, running_net_proceeds: 0 }, ...rows]
})

const crossoverAnnotations = computed(() => {
  const data = chart_data.value
  const annotations = []
  for (let i = 2; i < data.length; i++) {
    const prevBelow = data[i - 1].running_net_proceeds < data[i - 1].running_cost
    const currBelow = data[i].running_net_proceeds < data[i].running_cost
    if (prevBelow !== currBelow) {
      annotations.push({
        x: data[i].date,
        borderColor: '#775DD0',
        strokeDashArray: 0,
        label: {
          borderColor: '#775DD0',
          style: { color: '#fff', background: '#775DD0', fontSize: '10px' },
          text: !currBelow ? `Break Even ${data[i].date}` : `Crossed ${data[i].date}`,
          orientation: 'horizontal',
        },
      })
    }
  }
  return annotations
})

const chartOptions = computed(() => ({
  title: {
    text: 'Spend vs Proceeds'
  },
  chart: {
    height: 300,
    type: 'line',
    toolbar: { show: false },
    zoom: { enabled: false },
  },
  xaxis: {
    categories: chart_data.value.map(d => d.date),
    labels: { rotate: -45 },
  },
  yaxis: {
    min: 0,
    labels: { formatter: v => `$${parseFloat(v).toFixed(0)}` },
  },
  stroke: { curve: 'smooth', width: [3, 3] },
  colors: ['#FF4560', 'forestgreen'],
  annotations: { xaxis: crossoverAnnotations.value },
  tooltip: { y: { formatter: v => `$${parseFloat(v).toFixed(2)}` } },
  legend: { position: 'top' },
}))

const chartSeries = computed(() => [
  { name: 'Running Spend', data: chart_data.value.map(d => d.running_cost) },
  // { name: 'Running Net', data: chart_data.value.map(d => d.running_net) },
  { name: 'Running Net Proceeds', data: chart_data.value.map(d => d.running_net_proceeds) },
])

function prevPeriodLabel(date, iv) {
  if (iv === 'day') {
    const d = new Date(date + 'T00:00:00')
    d.setDate(d.getDate() - 1)
    return d.toISOString().slice(0, 10)
  }
  if (iv === 'month') {
    const [y, m] = date.split('-').map(Number)
    return m === 1 ? `${y - 1}-12` : `${y}-${String(m - 1).padStart(2, '0')}`
  }
  if (iv === 'week') {
    const [y, w] = date.split('-W').map(Number)
    return w === 0 ? `${y - 1}-W52` : `${y}-W${String(w - 1).padStart(2, '0')}`
  }
  if (iv === 'year') {
    const y = parseInt(date)
    return `${y - 1}`
  }
  if (iv === 'quarter') {
    const [y, q] = date.split('-W').map(Number)
    return q === 0 ? `${y - 1}-Q4` : `${y}-Q${String(q - 1).padStart(2, '0')}`
  }
  return ''
}

const inventory_data = computed(() => {
  if (!location_data.value) return []
  const rows = location_data.value.map(d => ({
    date: d.date,
    running_listed: parseInt(d.running_listed || 0),
    running_sold: parseInt(d.running_sold || 0),
  }))
  const zeroDate = rows.length ? prevPeriodLabel(rows[0].date, interval.value) : ''
  return [{ date: zeroDate, running_listed: 0, running_sold: 0 }, ...rows]
})

const inventoryOptions = computed(() => ({
  title: {
    text: 'Listed vs Sold'
  },
  chart: {
    height: 300,
    type: 'area',
    toolbar: { show: false },
    zoom: { enabled: false },
    stacked: false,
  },
  xaxis: {
    categories: inventory_data.value.map(d => d.date),
    labels: { rotate: -45 },
  },
  yaxis: {
    min: 0,
    labels: { formatter: v => Math.round(v) },
  },
  stroke: { curve: 'smooth', width: [2, 2] },
  colors: ['#f5a623', 'forestgreen'],
  fill: { type: 'solid', opacity: [0.25, 0.45] },
  dataLabels: { enabled: false },
  annotations: {},
  tooltip: { y: { formatter: v => `${Math.round(v)} items` } },
  legend: { position: 'top' },
}))

const inventorySeries = computed(() => [
  { name: 'Listed', data: inventory_data.value.map(d => d.running_listed) },
  { name: 'Sold', data: inventory_data.value.map(d => d.running_sold) },
])

const breakEvenDates = computed(() => {
  const data = chart_data.value
  const dates = []
  for (let i = 2; i < data.length; i++) {
    const prevBelow = data[i - 1].running_net_proceeds < data[i - 1].running_cost
    const currBelow = data[i].running_net_proceeds < data[i].running_cost
    if (prevBelow && !currBelow) dates.push(data[i].date)
  }
  return dates
})

const summary = computed(() => {
  if (!location_data.value?.length) return null
  const last = location_data.value[location_data.value.length - 1]
  const running_cost = parseFloat(last.running_cost || 0)
  const running_net = parseFloat(last.running_net || 0)
  const running_net_proceeds = parseFloat(last.running_net_proceeds || 0)
  const running_listed = parseInt(last.running_listed || 0)
  const running_sold = parseInt(last.running_sold || 0)
  const remaining = running_listed - running_sold
  const roi = running_cost > 0 ? (running_net_proceeds / running_cost) * 100 : 0
  const avg_net_per_sold = running_sold > 0 ? running_net / running_sold : 0
  const estimated_net = running_net + avg_net_per_sold * remaining
  return { running_cost, running_net_proceeds, roi, remaining, estimated_net }
})

function setActiveLoc(loc) {
  activeLoc.value = loc
  sourceData.value = {source_category_id: activeCat.value.id, source_location_id:loc.id}
}

function reset() {
  activeCat.value = null
  activeLoc.value = null
  resetSourceData()
}

function resetSourceData() {
  sourceData.value = {source_category_id: 0, source_location_id: 0}
  location_data.value = null
}

function selectCat(cat) {
  activeCat.value = cat
  resetSourceData()
  window.scrollTo({
    top: 0,
    left: 0,
    behavior: 'smooth'
  });
}

watch(sourceData, () => {
  fetchSourceData()
})
watch(interval, () => {
  fetchSourceData()
})


async function fetchSourceData() {
  loading.value = true
  try {
    location_data.value = await store.dispatch('eshop/getSourceHistory', {source_category_id: sourceData.value.source_category_id, source_location_id: sourceData.value.source_location_id, interval: interval.value})
  } catch (e) {
    console.log(e)
    location_data.value = null
  } finally {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });
    loading.value = false
  }
}

onMounted(fetchSourcesUsed)

async function fetchSourcesUsed() {
  try {
    sources_data.value = await store.dispatch('eshop/getSourcesUsed')
  } catch (e) {
    console.log(e)
    sources_data.value = []
  }
}

</script>

<style lang="scss" scoped>

</style>
